import React, { useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Truck } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { code: "es", label: "ES · Español" },
  { code: "en", label: "EN · English" },
  { code: "ar", label: "AR · العربية" },
];

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Show pending-approval banner if redirected here after Google login block
  const pendingParams = React.useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return { pending: p.get("pending") === "1", msg: p.get("msg") || "" };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(identifier, password);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail;
      if (status === 403 && msg) {
        toast.error(msg, { duration: 6000 });
      } else {
        toast.error(msg || t("login_error"));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-end mb-4">
          <select
            data-testid="lang-select-login"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="bg-white/10 text-white border border-white/20 rounded-md px-3 py-1.5 text-sm outline-none"
          >
            {LANGS.map(l => <option key={l.code} value={l.code} className="text-slate-900">{l.label}</option>)}
          </select>
        </div>

        <div className="text-center mb-6">
          {/* White pill so the logo stays vivid over the dark hero */}
          <div className="inline-flex items-center justify-center bg-white rounded-3xl px-8 py-5 shadow-[0_20px_50px_-10px_rgba(220,38,38,.35)] ring-1 ring-white/20 mb-4">
            <img src="/logo.png" alt="Meos Cargo" data-testid="logo-login" className="h-16 sm:h-20" />
          </div>
          <div className="font-display font-bold text-white text-2xl tracking-wide">MEOS CARGO</div>
          <p className="text-slate-300 text-sm mt-1" dir="rtl">ميدل ايست للشحن و التخليص الجمركي <span dir="ltr">world wide cargo</span></p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
          {pendingParams.pending && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="pending-banner">
              <div className="font-semibold mb-0.5">{t("account_pending")}</div>
              <div className="text-xs text-amber-800">{pendingParams.msg || t("account_pending_desc")}</div>
            </div>
          )}
          <h2 className="text-xl font-semibold text-slate-900 mb-6 text-center">{t("login_title")}</h2>

          <button
            data-testid="google-signin-btn"
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-slate-400 py-2.5 rounded-lg font-semibold text-slate-700 transition-colors mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.3-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.5-5.2l-6.2-5.3c-2 1.5-4.5 2.5-7.3 2.5-5.2 0-9.6-3.3-11.3-8L6 32.1c3.4 6.1 10 10.4 18 10.4z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.2 5.3c-.4.4 6.9-5 6.9-14.8 0-1.2-.1-2.4-.3-3.5z"/></svg>
            {t("google_signin")}
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-xs text-slate-400 uppercase tracking-wider">{t("or")}</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("user_or_email")}</label>
              <input
                data-testid="login-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg outline-none focus:border-red-600 focus:ring-2 focus:ring-blue-200"
                placeholder="admin / cliente / ventas..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("password")}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg outline-none focus:border-red-600 focus:ring-2 focus:ring-blue-200"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
            >
              {busy ? "..." : t("enter")}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
            <a href="/register" data-testid="register-link" className="text-red-600 font-semibold hover:underline">{t("new_client")}</a>
            <a href="/track" data-testid="public-track-link" className="text-red-600 font-semibold hover:underline">
              {t("public_tracking")} →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
